import { Elements } from '@stripe/react-stripe-js';
import { useAppState } from 'AppProvider';
import { Sep } from 'components/common/styled/common-styled';
import {
  CreditCardPayment,
  KICC,
  MemberChargePayment,
  RoomChargePayment,
} from 'features/ShoppingCart/components/PaymentMethods';
import PaymentAccordion from 'features/ShoppingCart/components/PaymentSection/PaymentAccordion';
import { HeadTitle } from 'features/ShoppingCart/utils/styles';
import { useFormContext, useWatch } from 'react-hook-form';
import useStripePromise from 'hooks/use-stripe-promise';
import useHyattCartValidation from 'hooks/useHyattCartValidation';
import useIsHyattLMSEnabledFlag from 'hooks/useIsHyattLMSEnabledFlag';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PaymentMethod } from 'types/payment';
import { getStripeElementOptions } from './PaymentSectionUtils';

const paymentMethodsComponents = {
  [PaymentMethod.KICC]: KICC,
  [PaymentMethod.ROOM_CHARGE]: RoomChargePayment,
  [PaymentMethod.MEMBER_NUMBER]: MemberChargePayment,
  [PaymentMethod.CREDIT_CARD]: CreditCardPayment,
  [PaymentMethod.HYATT_POINTS]: () => null,
  [PaymentMethod.CASH]: () => null,
};

const PaymentFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const PaymentLegend = styled.legend`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: var(--way-colors-textColor);
  margin-bottom: 24px;
`;

interface IPaymentSectionProps {
  paymentOptions: Array<{
    name: PaymentMethod;
    shouldValidateComplimentaryBooking: boolean;
  }>;
  paymentIntentSecret: string | null | undefined;
  totalCartAmountIsFree: boolean;
  listingIds: string[];
}

const PaymentSection = ({
  paymentOptions,
  paymentIntentSecret,
  totalCartAmountIsFree,
  listingIds,
}: IPaymentSectionProps) => {
  const isHyattLMSEnabled = useIsHyattLMSEnabledFlag();
  const { isEligible } = useHyattCartValidation(listingIds);
  const { theme } = useAppState();
  const { clearErrors, control, setValue } = useFormContext();
  const activeOption = useWatch({
    control,
    name: 'activeOption',
  }) as PaymentMethod | undefined;
  const { t: translateText } = useTranslation();
  const stripePromise = useStripePromise();
  const stripeElementOptions = getStripeElementOptions(
    paymentIntentSecret,
    theme,
  );

  const availablePaymentOptions = useMemo(() => {
    const options = [...paymentOptions];
    if (isHyattLMSEnabled && isEligible) {
      options.push({
        name: PaymentMethod.HYATT_POINTS,
        shouldValidateComplimentaryBooking: false,
      });
    }
    return options.filter(
      (po) => !totalCartAmountIsFree || po.shouldValidateComplimentaryBooking,
    );
  }, [paymentOptions, totalCartAmountIsFree, isHyattLMSEnabled, isEligible]);

  const selectPaymentMethod = useCallback(
    (paymentMethod: PaymentMethod | undefined) => {
      setValue('activeOption', paymentMethod);
      clearErrors([
        'card',
        'lastName',
        'paymentName',
        'reservationCode',
        'reservationNumber',
        'roomNumber',
      ]);
    },
    [setValue, clearErrors],
  );

  useEffect(() => {
    if (availablePaymentOptions.length === 0) {
      if (activeOption != null) {
        selectPaymentMethod(undefined);
      }
      return;
    }
    const availableNames = new Set(
      availablePaymentOptions.map(({ name }) => name),
    );
    if (activeOption == null) {
      selectPaymentMethod(availablePaymentOptions[0].name);
      return;
    }
    if (!availableNames.has(activeOption)) {
      selectPaymentMethod(availablePaymentOptions[0].name);
    }
  }, [activeOption, availablePaymentOptions, selectPaymentMethod]);

  const isActive = (methodName: PaymentMethod) => {
    return activeOption === methodName;
  };

  if (availablePaymentOptions.length === 0) {
    return null;
  }

  const renderPaymentMethods = () => (
    <>
      {availablePaymentOptions.length > 1 ? (
        <PaymentFieldset>
          <PaymentLegend className="page-heading">
            {translateText('paymentMethod')}
          </PaymentLegend>
          {availablePaymentOptions.map(
            ({ name: paymentMethod, shouldValidateComplimentaryBooking }) => {
              if (
                !totalCartAmountIsFree ||
                shouldValidateComplimentaryBooking
              ) {
                const Component = paymentMethodsComponents[paymentMethod];
                return (
                  <PaymentAccordion
                    key={paymentMethod}
                    charge={paymentMethod}
                    onSelectOption={() => selectPaymentMethod(paymentMethod)}
                    selected={isActive(paymentMethod)}
                  >
                    <Component hasMultiplePaymentMethods />
                  </PaymentAccordion>
                );
              }
              return null;
            },
          )}
        </PaymentFieldset>
      ) : (
        availablePaymentOptions.length > 0 && (
          <>
            <HeadTitle className="page-heading" style={{ marginBottom: 24 }}>
              {translateText('payment')}
            </HeadTitle>
            {availablePaymentOptions.map(
              ({ name: paymentMethod, shouldValidateComplimentaryBooking }) => {
                if (
                  !totalCartAmountIsFree ||
                  shouldValidateComplimentaryBooking
                ) {
                  const Component = paymentMethodsComponents[paymentMethod];
                  return <Component key={paymentMethod} />;
                }
                return null;
              },
            )}
          </>
        )
      )}

      {availablePaymentOptions.length > 0 && <Sep />}
    </>
  );

  if (paymentIntentSecret) {
    return (
      <CardContainer>
        <Elements options={stripeElementOptions} stripe={stripePromise}>
          {renderPaymentMethods()}
        </Elements>
      </CardContainer>
    );
  }

  return <CardContainer>{renderPaymentMethods()}</CardContainer>;
};

const CardContainer = styled.div`
  width: 100%;
  margin-bottom: 20px;

  & > div:last-child {
    margin-top: 16px;
    margin-bottom: 24px;
  }
`;

export default PaymentSection;
